import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col justify-between px-[34px]" }
const _hoisted_2 = { class: "flex flex-col mt-12 mb-10 items-center" }
const _hoisted_3 = { class: "flex items-center justify-center mb-4" }
const _hoisted_4 = { class: "font-sourcesanspro-semibold text-lg2" }
const _hoisted_5 = {
  key: 0,
  class: "flex items-center justify-center mb-4"
}
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { class: "mb-8" }
const _hoisted_8 = {
  key: 0,
  class: "mb-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg"
}
const _hoisted_9 = { class: "text-white/80 text-sm" }
const _hoisted_10 = { class: "flex items-center mb-4" }
const _hoisted_11 = { class: "font-sourcesanspro-semibold text-lg inline" }
const _hoisted_12 = {
  key: 2,
  class: "mb-8 text-center"
}
const _hoisted_13 = { class: "text-white/70" }
const _hoisted_14 = { class: "mb-8" }
const _hoisted_15 = { class: "flex w-full items-center justify-start" }
const _hoisted_16 = { class: "text-m2" }

import { useRoute, useRouter } from 'vue-router';
import { computed, onMounted, ref } from 'vue';
import { IonButton, IonContent, IonIcon, IonSpinner } from '@ionic/vue';
import { closeOutline, ticketOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';

import { useCart } from '@/modules/shop/composables/useCart';
import { GenericProduct } from '@/shared/interfaces/genericProduct';
import ItemAdder from '@/shared/components/ui/ItemAdder.vue';
import useQueue from '@/modules/queue/composables/useQueue';
import useToast from '@/shared/composables/useToast';
import { useCheckout } from '@/shared/composables/useCheckout';
import { TransactionType } from '@/enums/wallet.enum';
import { useCartStore } from '@/stores/shop/cartStore';
import { anykrowdApi } from '@/api/anykrowdApi';

// Init

export default /*@__PURE__*/_defineComponent({
  __name: 'QueueTicketsBuyPage',
  setup(__props) {

const { t } = useI18n();
const route = useRoute();
const activityId = +route.params.activityId;
const selectedDate = route.query.date as string | undefined;
const selectedTimeSlot = route.query.timeSlot as string | undefined;
const isFromUnbooked = route.query.from_unbooked === 'true';
const bookingEventTicketIds = ref<number[]>([]);
const selectedSchedule = ref<any>(null);
const isLoadingSchedule = ref(false);

const { getQueueActivity, bookEventTicketSlot } = useQueue();
const { activity, isFetchedActivity } = getQueueActivity(activityId);
const { getCartTotalItemsByCategory, addToCart, decreaseProductQuantity, getCartItems, clearCart } = useCart();
const { presentToast } = useToast();
const { processCheckout } = useCheckout();
const router = useRouter();

// Load schedule data for availability checking
const loadScheduleAvailability = async () => {
	if (!selectedDate || !activityId) return;

	isLoadingSchedule.value = true;
	try {
		const schedulesResponse = await anykrowdApi.get(`/queue/activities/${activityId}/schedules`, true);
		const schedules = schedulesResponse.data.schedules || [];

		// Find the matching schedule for selected date/time
		if (selectedTimeSlot) {
			// For time slot queues, match by date and time slot
			const [startTime, endTime] = selectedTimeSlot.split('-');
			// Normalize time format (handle both "HH:MM" and "HH:MM:SS")
			const normalizeTime = (time: string) => time.substring(0, 5); // Take only HH:MM
			selectedSchedule.value = schedules.find((s: any) =>
				s.date === selectedDate &&
				normalizeTime(s.start_time || '') === normalizeTime(startTime) &&
				normalizeTime(s.end_time || '') === normalizeTime(endTime)
			);
		} else {
			// For chronological queues, match by date only
			selectedSchedule.value = schedules.find((s: any) => s.date === selectedDate);
		}
	} catch (error) {
		console.error('Error loading schedule availability:', error);
	} finally {
		isLoadingSchedule.value = false;
	}
};

// Get availability for a specific queue type (normal or fastlane)
const getAvailabilityForQueueType = (queueType: 'normal' | 'fastlane') => {
	if (!selectedSchedule.value?.availability) return null;

	const queueTypeKey = queueType === 'normal' ? 'normal' : 'fastlane';
	return selectedSchedule.value.availability[queueTypeKey] || null;
};

// Check if a ticket type is sold out
const isTicketTypeSoldOut = (queueType: 'normal' | 'fastlane') => {
	if (!selectedSchedule.value) return false; // Don't show as sold out if schedule not loaded yet
	const availability = getAvailabilityForQueueType(queueType);
	if (!availability) return false;
	return availability.available <= 0;
};

// Get available count for a queue type
const getAvailableCount = (queueType: 'normal' | 'fastlane') => {
	if (!selectedSchedule.value) return 999; // Return high number if schedule not loaded yet (to avoid false sold out)
	const availability = getAvailabilityForQueueType(queueType);
	return availability?.available ?? 0;
};

// Clear queue tickets from cart on component mount to avoid stale items
onMounted(async () => {
	const cartStore = useCartStore();
	// Remove only queue_ticket items from cart, keep other categories
	cartStore.cart = cartStore.cart.filter((item: any) => item.category !== 'queue_ticket');

	// Load event ticket IDs if booking from unbooked tickets
	if (isFromUnbooked) {
		const storedIds = sessionStorage.getItem('booking_event_ticket_ids');
		if (storedIds) {
			try {
				bookingEventTicketIds.value = JSON.parse(storedIds);
			} catch (e) {
				console.error('Error parsing booking event ticket IDs:', e);
			}
		}
	}

	// Load schedule availability if we have date/time selected
	if (selectedDate) {
		await loadScheduleAvailability();
	}
});

// Create ticket type objects that match the expected interface
const queueTicketTypes = computed(() => {
	if (!activity.value) return [];

	const types = [];

	// Regular pass
	if (activity.value.regular_price && activity.value.regular_currency_id) {
		const isSoldOut = isTicketTypeSoldOut('normal');
		const availableCount = getAvailableCount('normal');
		const soldOutLabel = isSoldOut ? ` (${t('queue.sold_out')})` : '';
		const availabilityLabel = !isSoldOut && availableCount > 0 && availableCount <= 5
			? ` (${availableCount} ${t('queue.available')})`
			: '';

		const regularProduct = new GenericProduct({
			id: activityId * 1000 + 1, // Generate unique ID for regular pass
			name: t('queue.regular_pass') + soldOutLabel,
			description: t('queue.regular_pass_description') + availabilityLabel,
			price: activity.value.regular_price / 100, // Convert from cents
			currency_id: activity.value.regular_currency_id,
			currency: activity.value.regularCurrency || {
				id: activity.value.regular_currency_id,
				prefix: '€',
				suffix: '',
				exchange_rate: 1
			},
			vat_percentage: 0,
			order: 1
		});
		(regularProduct as any).queue_type = 'regular';
		(regularProduct as any).isSoldOut = isSoldOut;
		types.push(regularProduct);
	}

	// Priority pass (fastlane)
	if (activity.value.priority_price && activity.value.priority_currency_id) {
		const isSoldOut = isTicketTypeSoldOut('fastlane');
		const availableCount = getAvailableCount('fastlane');
		const soldOutLabel = isSoldOut ? ` (${t('queue.sold_out')})` : '';
		const availabilityLabel = !isSoldOut && availableCount > 0 && availableCount <= 5
			? ` (${availableCount} ${t('queue.available')})`
			: '';

		const priorityProduct = new GenericProduct({
			id: activityId * 1000 + 2, // Generate unique ID for priority pass
			name: t('queue.priority_pass') + soldOutLabel,
			description: t('queue.priority_pass_description') + availabilityLabel,
			price: activity.value.priority_price / 100, // Convert from cents
			currency_id: activity.value.priority_currency_id,
			currency: activity.value.priorityCurrency || {
				id: activity.value.priority_currency_id,
				prefix: '€',
				suffix: '',
				exchange_rate: 1
			},
			vat_percentage: 0,
			order: 2
		});
		(priorityProduct as any).queue_type = 'fastlane';
		(priorityProduct as any).isSoldOut = isSoldOut;
		types.push(priorityProduct);
	}

	return types;
});

// Helper to get the count of a specific queue ticket type in cart
const getQueueTicketCount = (queueTicketType: any) => {
	const cartItems = getCartItems('queue_ticket');
	const matchingItems = cartItems.filter((cartItem: any) =>
		cartItem.product.id === queueTicketType.id
	);
	return matchingItems.reduce((sum: number, item: any) => sum + item.quantity, 0);
};

// Handle decrease product
const onDecreaseProductQuantity = (item: any) => {
	decreaseProductQuantity(item);
};

// Get the max bookings limit from activity
// Note: 0 means unlimited, so we use a high number (999) as the practical limit
const maxBookingsPerUser = computed(() => {
	if (!activity.value) return 10;
	const limit = activity.value.max_bookings_per_user;
	// If limit is 0, treat as unlimited (use 999 as practical limit)
	return limit === 0 ? 999 : limit;
});

// Handle increase product
const onIncreaseProductQuantity = async (item: any) => {
	const currentCount = getQueueTicketCount(item);
	const maxLimit = maxBookingsPerUser.value;

	// Check if ticket type is sold out
	const queueType = (item as any).queue_type;
	if (queueType === 'regular' || queueType === 'normal') {
		if (isTicketTypeSoldOut('normal')) {
			await presentToast('top', t('queue.error_sold_out'), 5000, 'danger');
			return;
		}
		const availableCount = getAvailableCount('normal');
		if (availableCount > 0 && currentCount >= availableCount) {
			await presentToast('top', `${t('queue.error_sold_out')} (${availableCount} ${t('queue.available') || 'available'})`, 5000, 'danger');
			return;
		}
	} else if (queueType === 'fastlane') {
		if (isTicketTypeSoldOut('fastlane')) {
			await presentToast('top', t('queue.error_sold_out'), 5000, 'danger');
			return;
		}
		const availableCount = getAvailableCount('fastlane');
		if (availableCount > 0 && currentCount >= availableCount) {
			await presentToast('top', `${t('queue.error_sold_out')} (${availableCount} ${t('queue.available') || 'available'})`, 5000, 'danger');
			return;
		}
	}

	if (currentCount >= maxLimit) {
		const message = maxLimit === 1
			? t('queue.ticket_limit_reached')
			: `${t('queue.ticket_limit_reached')} (Maximum: ${maxLimit})`;
		await presentToast('top', message, 5000, 'danger');
		return;
	}

	addToCart(item, 'queue_ticket', {
		activityId: activityId,
		queueType: (item as any).queue_type || (item as any).queueType
	});
};

const canAddMore = (item: any) => {
	const currentCount = getQueueTicketCount(item);
	const maxLimit = maxBookingsPerUser.value;

	// Check if ticket type is sold out
	const queueType = (item as any).queue_type;
	if (queueType === 'regular' || queueType === 'normal') {
		if (isTicketTypeSoldOut('normal')) {
			return false;
		}
		// Also check availability limit
		const availableCount = getAvailableCount('normal');
		if (availableCount > 0 && currentCount >= availableCount) {
			return false;
		}
	} else if (queueType === 'fastlane') {
		if (isTicketTypeSoldOut('fastlane')) {
			return false;
		}
		// Also check availability limit
		const availableCount = getAvailableCount('fastlane');
		if (availableCount > 0 && currentCount >= availableCount) {
			return false;
		}
	}

	return currentCount < maxLimit;
};

// Handle checkout
const handleCheckout = async () => {
	// If booking from unbooked event tickets, use the booking endpoint directly
	if (isFromUnbooked && bookingEventTicketIds.value.length > 0 && selectedDate) {
		// Book each event ticket
		let successCount = 0;
		let errorCount = 0;

		for (const eventTicketId of bookingEventTicketIds.value) {
			try {
				await bookEventTicketSlot(eventTicketId, {
					target_date: selectedDate,
					target_time_slot: selectedTimeSlot,
				});
				successCount++;
			} catch (error) {
				console.error(`Failed to book event ticket ${eventTicketId}:`, error);
				errorCount++;
			}
		}

		// Clear stored IDs
		sessionStorage.removeItem('booking_event_ticket_ids');
		sessionStorage.removeItem('unbooked_event_ticket_ids');

		// Show result message
		if (successCount > 0 && errorCount === 0) {
			await presentToast('top', t('queue.all_tickets_booked_successfully'), 3000, 'success');
			router.push('/my-queues');
		} else if (successCount > 0 && errorCount > 0) {
			await presentToast('top', t('queue.some_tickets_booked_successfully', { success: successCount, total: bookingEventTicketIds.value.length }), 5000, 'warning');
			router.push('/my-queues');
		} else {
			await presentToast('top', t('queue.booking_failed'), 5000, 'danger');
		}

		return;
	}

	// Regular checkout flow for new bookings
	await processCheckout('QUEUE_BOOKING', {
		activityId,
		selectedDate,
		selectedTimeSlot
	});
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonContent), null, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_unref(IonButton), {
              class: "rounded ion-no-padding ion-no-margin w-[60px] h-[60px] hover:rounded-full hover:bg-white/20",
              routerLink: `/queue/${activityId}`
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(IonIcon), {
                  class: "text-[38px] text-white cursor-pointer",
                  icon: _unref(closeOutline)
                }, null, 8, ["icon"])
              ]),
              _: 1
            }, 8, ["routerLink"])
          ]),
          _createElementVNode("h1", _hoisted_4, _toDisplayString(_unref(activity)?.name), 1)
        ]),
        (!_unref(isFetchedActivity))
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(_unref(IonSpinner), { name: "dots" })
            ]))
          : _createCommentVNode("", true),
        (_unref(activity) && _unref(isFetchedActivity) && queueTicketTypes.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                (isFromUnbooked && bookingEventTicketIds.value.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                      _createElementVNode("p", _hoisted_9, _toDisplayString(_unref(t)('queue.booking_unbooked_tickets', { count: bookingEventTicketIds.value.length })), 1)
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_10, [
                  _createVNode(_unref(IonIcon), {
                    class: "mr-2 text-[20px] text-white cursor-pointer",
                    icon: _unref(ticketOutline)
                  }, null, 8, ["icon"]),
                  _createElementVNode("h2", _hoisted_11, _toDisplayString(_unref(t)('queue.choose_your_pass')), 1)
                ]),
                _createElementVNode("div", null, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(queueTicketTypes.value, (queueTicketType, idx) => {
                    return (_openBlock(), _createBlock(ItemAdder, {
                      key: idx,
                      onOnDecreaseProductQuantity: onDecreaseProductQuantity,
                      onOnIncreaseProductQuantity: onIncreaseProductQuantity,
                      id: queueTicketType.id,
                      description: queueTicketType.description,
                      expandable: queueTicketType.description ? true : false,
                      productId: queueTicketType.id,
                      product: queueTicketType,
                      class: "mb-4",
                      title: queueTicketType.name,
                      price: queueTicketType.price,
                      currency: queueTicketType.currency,
                      quantity: getQueueTicketCount(queueTicketType),
                      "can-add-more": canAddMore(queueTicketType)
                    }, null, 8, ["id", "description", "expandable", "productId", "product", "title", "price", "currency", "quantity", "can-add-more"]))
                  }), 128))
                ])
              ])
            ]))
          : (_unref(activity) && _unref(isFetchedActivity) && queueTicketTypes.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                _createElementVNode("p", _hoisted_13, _toDisplayString(_unref(t)('queue.no_ticket_types_available')), 1)
              ]))
            : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_14, [
          (_unref(isFetchedActivity))
            ? (_openBlock(), _createBlock(_unref(IonButton), {
                key: 0,
                disabled: isFromUnbooked ? false : _unref(getCartTotalItemsByCategory)('queue_ticket') === 0,
                class: "w-full hover:rounded-full hover:bg-white",
                expand: "full",
                size: "large",
                shape: "round",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (handleCheckout()))
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_15, [
                    _createElementVNode("span", _hoisted_16, _toDisplayString(isFromUnbooked ? _unref(t)('queue.book_now') : _unref(t)('queue.continue_to_checkout')), 1)
                  ])
                ]),
                _: 1
              }, 8, ["disabled"]))
            : _createCommentVNode("", true)
        ])
      ])
    ]),
    _: 1
  }))
}
}

})